import React, { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter, Link } from '../../router/Router';
import { validateEmail } from '../../utils/errors';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, ArrowLeft, UserPlus, Sparkles, CheckCircle2, Phone, GraduationCap } from 'lucide-react';
import { referralService } from '../../services/referralService';
import { auth } from '../../firebase/config/firebase';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import { universityService } from '../../services/universityService';

const WhatsAppIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    style={{ flexShrink: 0 }}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.456 5.711 1.457h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const Register: React.FC = () => {
  const { register, isLoading, error, clearError } = useAuth();
  const { navigate, queryParams } = useRouter();

  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  // University Selection State
  const [allUniversities, setAllUniversities] = useState<{ id: string; name: string; governorate: string }[]>(SEED_UNIVERSITIES);
  const [universityId, setUniversityId] = useState('');
  const [customUniversityName, setCustomUniversityName] = useState('');

  // Load active universities from Firestore service & combine with SEED_UNIVERSITIES
  useEffect(() => {
    const fetchUnis = async () => {
      try {
        const list = await universityService.getActiveUniversities();
        if (list && list.length > 0) {
          const map = new Map<string, { id: string; name: string; governorate: string }>();
          SEED_UNIVERSITIES.forEach((u) => map.set(u.id, { id: u.id, name: u.name, governorate: u.governorate }));
          list.forEach((u) => map.set(u.id, { id: u.id, name: u.name, governorate: u.governorate }));
          setAllUniversities(Array.from(map.values()));
        }
      } catch {
        // Fallback to SEED_UNIVERSITIES
      }
    };
    fetchUnis();
  }, []);

  // Referral / Promo Code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState<{
    isValid: boolean;
    inviterName?: string;
    checked: boolean;
  }>({ isValid: false, checked: false });
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  // Auto-detect promo code from URL query ?ref=XYZ
  useEffect(() => {
    let urlRef = '';
    if (typeof window !== 'undefined') {
      urlRef = new URLSearchParams(window.location.search).get('ref') || '';
    }
    if (!urlRef && queryParams?.ref) {
      urlRef = String(queryParams.ref);
    }

    if (urlRef) {
      const formatted = urlRef.trim().toUpperCase();
      setPromoCode(formatted);
      checkPromo(formatted);
    }
  }, [queryParams]);

  const checkPromo = async (code: string) => {
    const clean = code.trim().toUpperCase();
    if (!clean || clean.length < 4) {
      setPromoValidation({ isValid: false, checked: false });
      return;
    }
    setIsValidatingPromo(true);
    try {
      const res = await referralService.validateReferralCode(clean);
      setPromoValidation({
        isValid: res.valid,
        inviterName: res.inviterName,
        checked: true,
      });
    } catch {
      setPromoValidation({ isValid: false, checked: true });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setClientError(null);
    clearError();

    // Validations
    if (!fullName.trim()) {
      setClientError('يرجى إدخال اسم الطالب بالكامل.');
      return;
    }

    if (fullName.trim().split(' ').length < 2) {
      setClientError('يرجى إدخال الاسم الثنائي على الأقل (مثال: أحمد محمد).');
      return;
    }

    // Phone validation (Mandatory)
    const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
    if (!cleanPhone) {
      setClientError('يرجى إدخال رقم الهاتف المحمول / الواتساب (إجباري).');
      return;
    }

    if (!/^01[0125][0-9]{8}$/.test(cleanPhone)) {
      setClientError('يرجى إدخال رقم هاتف محمول مصري صحيح مكون من 11 رقماً يبدأ بـ 010 أو 011 أو 012 أو 015.');
      return;
    }

    if (!email.trim()) {
      setClientError('يرجى إدخال البريد الإلكتروني.');
      return;
    }

    if (!validateEmail(email)) {
      setClientError('صيغة البريد الإلكتروني غير صحيحة (مثال: student@example.com).');
      return;
    }

    if (!password) {
      setClientError('يرجى إدخال كلمة المرور.');
      return;
    }

    if (password.length < 6) {
      setClientError('يجب ألا تقل كلمة المرور عن 6 خانات.');
      return;
    }

    if (password !== confirmPassword) {
      setClientError('كلمتا المرور غير متطابقتين. يرجى إعادة التأكد منهما.');
      return;
    }

    if (!universityId) {
      setClientError('يرجى اختيار الجامعة أو الأكاديمية المقيد بها.');
      return;
    }

    if (universityId === 'other' && !customUniversityName.trim()) {
      setClientError('يرجى كتابة اسم الجامعة أو الأكاديمية يدوياً في الخانة المخصصة.');
      return;
    }

    const selectedUni = allUniversities.find((u) => u.id === universityId);
    const resolvedUniversityName =
      universityId === 'other'
        ? customUniversityName.trim()
        : selectedUni?.name || 'جامعة غير محددة';
    const resolvedGovernorate =
      universityId === 'other'
        ? 'أخرى'
        : selectedUni?.governorate || 'مصر';

    const success = await register({
      fullName: fullName.trim(),
      phoneNumber: cleanPhone,
      email: email.trim(),
      password,
      confirmPassword,
      universityName: resolvedUniversityName,
      universityId: universityId === 'other' ? `uni_unlisted_${Date.now()}` : universityId,
      universityGovernorate: resolvedGovernorate,
    });

    if (success) {
      // Connect referral code if provided
      const currentUid = auth.currentUser?.uid;
      if (currentUid && promoCode.trim()) {
        try {
          await referralService.registerReferral(
            currentUid,
            fullName.trim(),
            undefined,
            promoCode.trim().toUpperCase()
          );
        } catch (refErr) {
          console.warn('Non-blocking referral connection error:', refErr);
        }
      }

      // Automatic routing to Profile Completion step
      navigate('/complete-profile');
    }
  };

  const activeError = clientError || error?.messageAr;

  return (
    <div className="auth-page-wrapper" dir="rtl">
      <div className="auth-card">
        {/* Header / Brand */}
        <div className="auth-card-header">
          <div className="auth-brand-badge" style={{ padding: '0', overflow: 'hidden', width: '56px', height: '56px', borderRadius: '16px', margin: '0 auto 1rem' }}>
            <img src="/oto-logo.jpg" alt="OTO" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <h1 className="auth-title">إنشاء حساب طالب جديد</h1>
          <p className="auth-subtitle">
            سجل الآن في <strong>OTO STUDENTS</strong> لحجز مقعدك لجامعتك من الإسكندرية
          </p>
        </div>

        {/* Error Alert */}
        {activeError && (
          <div className="auth-error-banner" role="alert">
            <AlertCircle className="auth-error-icon" size={20} />
            <span>{activeError}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          {/* Full Name */}
          <div className="form-group">
            <label htmlFor="register-name" className="form-label">
              الاسم بالكامل
            </label>
            <div className="input-with-icon">
              <User className="input-icon" size={18} />
              <input
                id="register-name"
                type="text"
                className="form-input"
                placeholder="أحمد محمد علي"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                autoFocus
              />
            </div>
          </div>

          {/* Phone Number (Mandatory) */}
          <div className="form-group">
            <label htmlFor="register-phone" className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>رقم الهاتف المحمول (الواتساب)</span>
              <span style={{ color: '#E5232A', fontSize: '11px', fontWeight: 'bold' }}>* إجباري</span>
            </label>
            <div className="input-with-icon">
              <Phone className="input-icon" size={18} />
              <input
                id="register-phone"
                type="tel"
                className="form-input"
                placeholder="01012345678"
                dir="ltr"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {/* University Selection (Mandatory) */}
          <div className="form-group">
            <label htmlFor="register-university" className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>اسم الجامعة / الأكاديمية المقيد بها</span>
              <span style={{ color: '#E5232A', fontSize: '11px', fontWeight: 'bold' }}>* إجباري</span>
            </label>
            <div className="input-with-icon">
              <GraduationCap className="input-icon" size={18} />
              <select
                id="register-university"
                className="form-input"
                style={{ paddingRight: '2.5rem', cursor: 'pointer' }}
                value={universityId}
                onChange={(e) => {
                  setUniversityId(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                required
              >
                <option value="">-- اختر جامعتك أو أكاديميتك --</option>
                {allUniversities.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.governorate})
                  </option>
                ))}
                <option value="other">🏛️ جامعة أو أكاديمية أخرى (كتابة يدوية)</option>
              </select>
            </div>
          </div>

          {/* Custom University Name Input (if Other selected) */}
          {universityId === 'other' && (
            <div className="form-group">
              <label htmlFor="register-custom-uni" className="form-label">
                اكتب اسم الجامعة أو الأكاديمية يدوياً:
              </label>
              <div className="input-with-icon">
                <GraduationCap className="input-icon" size={18} />
                <input
                  id="register-custom-uni"
                  type="text"
                  className="form-input"
                  placeholder="مثال: جامعة برج العرب التكنولوجية"
                  value={customUniversityName}
                  onChange={(e) => {
                    setCustomUniversityName(e.target.value);
                    if (clientError) setClientError(null);
                  }}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>
          )}

          {/* Email */}
          <div className="form-group">
            <label htmlFor="register-email" className="form-label">
              البريد الإلكتروني
            </label>
            <div className="input-with-icon">
              <Mail className="input-icon" size={18} />
              <input
                id="register-email"
                type="email"
                className="form-input"
                placeholder="student@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="register-password" className="form-label">
              كلمة المرور (6 خانات على الأقل)
            </label>
            <div className="input-with-icon">
              <Lock className="input-icon" size={18} />
              <input
                id="register-password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="register-confirm-password" className="form-label">
              تأكيد كلمة المرور
            </label>
            <div className="input-with-icon">
              <Lock className="input-icon" size={18} />
              <input
                id="register-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
                aria-label={showConfirmPassword ? 'إخفاء تأكيد كلمة المرور' : 'إظهار تأكيد كلمة المرور'}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Referral / Promo Code (Optional) */}
          <div className="form-group">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="register-promo" className="form-label text-xs font-bold text-slate-700 m-0">
                كود الدعوة / البرومو كود <span className="text-slate-400 font-normal">(اختياري)</span>
              </label>
              {promoValidation.isValid && (
                <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 size={13} />
                  كود صالح
                </span>
              )}
            </div>
            <div className="input-with-icon">
              <Sparkles className="input-icon text-indigo-500" size={18} />
              <input
                id="register-promo"
                type="text"
                className="form-input font-mono font-bold tracking-wider uppercase"
                placeholder="مثال: OTO-7890"
                value={promoCode}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  setPromoCode(val);
                  checkPromo(val);
                }}
                onBlur={() => checkPromo(promoCode)}
                disabled={isLoading}
                autoComplete="off"
              />
            </div>
            {isValidatingPromo && (
              <span className="text-[11px] text-slate-400 block mt-1">جاري التحقق من كود الزميل...</span>
            )}
            {promoValidation.checked && promoValidation.isValid && (
              <div className="mt-1.5 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
                <span>
                  كود صالح! دعوة من الزميل: <strong>{promoValidation.inviterName || 'طالب OTO'}</strong>.
                </span>
              </div>
            )}
            {promoValidation.checked && !promoValidation.isValid && promoCode.trim().length >= 4 && (
              <span className="text-[11px] text-rose-500 block mt-1">
                كود الدعوة غير مسجل، يمكنك إتمام التسجيل بدونه.
              </span>
            )}
            {!promoCode && (
              <span className="text-[11px] text-slate-500 block mt-1">
                إذا أرسل لك زميلك رابط أو كود دعوة، أدخله هنا للاستفادة من مزايا الحجز.
              </span>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="btn-primary auth-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="btn-loading-content">
                <span className="spinner-inline"></span>
                جاري إنشاء الحساب...
              </span>
            ) : (
              <span className="btn-normal-content">
                <UserPlus size={18} />
                إنشاء حساب
              </span>
            )}
          </button>
        </form>

        {/* Footer switch to login */}
        <div className="auth-card-footer">
          <p>
            لديك حساب بالفعل؟{' '}
            <Link to="/login" className="auth-switch-link">
              تسجيل الدخول
            </Link>
          </p>
          <div className="back-home-link-box">
            <Link to="/" className="back-home-link">
              <ArrowLeft size={16} />
              العودة للرئيسية
            </Link>
          </div>

          {/* WhatsApp Contact Support Button */}
          <div className="auth-whatsapp-box">
            <a
              href={`https://wa.me/201207337330?text=${encodeURIComponent('مرحباً خدمة العملاء، أحتاج مساعدة بخصوص إنشاء حساب جديد في منصة oto students')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp-auth"
              title="تواصل مباشرة عبر واتساب"
            >
              <WhatsAppIcon size={20} />
              <span>تواصل مع الدعم عبر واتساب (01207337330)</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
